import { Component } from '@theme/component';

/**
 * A custom element that renders a video background only when it is close to the viewport.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #observer;
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    videoElement.preload = 'none';
    videoElement.pause();

    if (!('IntersectionObserver' in window)) {
      this.#loadAndPlay();
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          this.#loadAndPlay();
        } else {
          this.#pause();
        }
      },
      {
        rootMargin: '500px 0px',
        threshold: 0.01,
      }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#pause();
  }

  #loadAndPlay() {
    const { videoSources, videoElement } = this.refs;

    if (!this.#loaded) {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource) source.setAttribute('src', videoSource);
      }

      videoElement.preload = 'metadata';
      videoElement.load();
      this.#loaded = true;
    }

    if (document.hidden) return;
    videoElement.play().catch(() => {});
  }

  #pause() {
    this.refs.videoElement?.pause();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
